import React from 'react'
import IconeArte from '../../lib/iconeArte'
import SilhuetaBlumenau from '../SilhuetaBlumenau'
import EstradaNeon from '../EstradaNeon'
import { comDestaque } from '../../lib/destaque'
import { useEntrada, atraso } from '../../lib/entrada'
import { PUBLICO_ALVO, ANCORA_INSCRICAO } from '../../dados/evento'

/* Os três selos dos perfis são RECORTES DA FOLHA DE ÍCONES DO KIT
   (`static/img/icons-7-web.png`), não SVG traçado.

   Onde o traço Tabler serve bem — ícone pequeno, monocromático, ao lado de
   texto — ele continua no resto da página. Aqui não: os três aparecem a ~68px,
   são o único elemento gráfico da coluna, e o disco de neon com halo azul da
   arte é justamente o que amarra a dobra ao material impresso do evento.

   Recortados por caixa explícita e quantizados sem dither (48 cores, ~40KB
   cada a 192px, que cobre 2,8x de densidade). Ver o script em
   scratchpad/recortar.py e a receita em [[nauta-rota-assets-unoptimized]]. */
const SELOS_PERFIL = ['ico-pessoa', 'ico-grupo', 'ico-barras']

/**
 * 4ª dobra — para quem é o evento.
 *
 * ⚠️ REDESENHADA EM 12/08 PARA A ARTE APROVADA (a lâmina "Eficiência não é
 * tecnologia isolada"). A versão anterior era uma composição editorial em duas
 * colunas — citação grande à esquerda, três perfis como LINHAS numeradas à
 * direita — e é justamente o oposto do que a arte pede.
 *
 * O que a arte manda, e o que passou a valer aqui:
 *   · a frase de conceito é o TÍTULO da dobra, centralizado e em duas cores,
 *     não uma citação encostada na margem esquerda;
 *   · os três perfis são CARTÕES iguais, lado a lado, cada um com o selo
 *     circular do ícone no topo, o nome do perfil em azul, um fio divisor e a
 *     frase centralizada abaixo — nenhuma numeração, nenhum avatar;
 *   · o CTA fecha a dobra centralizado e em largura generosa, não como botão
 *     vazado colado embaixo da citação.
 *
 * O `avatar` de cada perfil continua em `dados/evento.js` e deixou de ser
 * renderizado: na arte o Nauta aparece UMA vez por dobra, como personagem da
 * cena (aqui, no canto inferior direito), e não recortado dentro de cada card.
 */
export default function SecaoPublico({
  conceito = 'Eficiência não é tecnologia isolada. É a integração entre pessoas, processos, dados e gestão.',
  conceitoDestaque = 'pessoas, processos, dados e gestão.',
  /* Era "O Rota da Eficiência Apresenta.me faz sentido para:" — dois nomes
     próprios encostados, sem conector, lendo como um nome só. */
  subtitulo = 'O Rota da Eficiência, da Apresenta.me, faz sentido para:',
  perfis = PUBLICO_ALVO,
  textoCta = 'Garantir minha presença',
  mascote = '/img/nauta-rota-2-web.png',
}) {
  const ref = useEntrada()

  return (
    <section className="secao secao-publico noite" id="para-quem" ref={ref}>
      {/* Cena de fundo da lâmina: horizonte de Blumenau, a rota subindo pela
          direita e o Nauta plantando a bandeira no canto. Tudo decorativo e
          fora do fluxo — o conteúdo é o bloco de duas colunas por cima. */}
      <SilhuetaBlumenau className="silhueta secao-publico-silhueta" />
      <EstradaNeon className="publico-rota" />
      {mascote && (
        <img className="publico-nauta" src={mascote} alt="" width="420" height="631" loading="lazy" />
      )}

      <div className="container-site publico-interno">
        <div className="cabecalho-secao cabecalho-centro entra">
          <h2 className="publico-conceito">{comDestaque(conceito, conceitoDestaque)}</h2>
        </div>

        {/* A LINHA DE APOIO SAIU DO CABEÇALHO em 13/08, a pedido do cliente
            ("faz sentido para poder estar no topo da coluna direita com uma
            fonte um pouco menor, não dando tanto destaque").

            Debaixo do conceito ela disputava atenção com um título de 3rem e
            ficava órfã: anunciava uma lista que estava do outro lado da dobra.
            Como primeira linha da coluna da direita ela vira o que sempre foi —
            a abertura da lista de perfis. É irmã direta de `.publico-interno`
            porque a colocação nas colunas é feita pela grade da dobra — ver
            "PARA QUEM É: a linha de apoio…" em revisao.css, onde as quatro
            posições ficam explícitas. */}
        {/* A COLUNA DA DIREITA É UM ITEM SÓ (13/08). A linha de apoio e a lista
            eram dois itens de grade irmãos, então a primeira linha da grade
            ficava com a altura do CONCEITO (211px) e a linha de apoio, de 25px,
            flutuava sozinha no topo dela — o vão que aparece na captura do
            cliente entre a frase e "Donos e Sócios".

            Com o embrulho, a direita empilha no fluxo normal e a esquerda fica
            livre para colocar o botão embaixo do título sem que uma coluna
            imponha altura de linha à outra. */}
        <div className="publico-coluna">
          <p className="publico-subtitulo entra">{subtitulo}</p>

        {/* LINHAS, NÃO CARTÕES (12/08). Eram três cartões iguais lado a lado —
            o tell de layout mais comum que existe, e o terceiro da página a
            usar a mesma fileira. Como os três perfis têm exatamente o mesmo
            peso, a moldura individual não comunicava hierarquia nenhuma: só
            desenhava três caixas. O que separa um do outro agora é o fio e o
            espaço. */}
          <ul className="publico-lista lista-limpa">
          {perfis.map((perfil, i) => {
            const selo = SELOS_PERFIL[i] || SELOS_PERFIL[0]
            return (
              <li key={perfil.titulo} className="entra" style={atraso(i + 1, 70)}>
                <article className="publico-perfil">
                  <span className="v2-selo-arte" aria-hidden="true">
                    <IconeArte nome={selo} />
                  </span>
                  <h3>{perfil.titulo}</h3>
                  <p>{perfil.texto}</p>
                </article>
              </li>
            )
          })}
          </ul>
        </div>

        <div className="secao-acao entra">
          {/* BOTÃO CHEIO (13/08, pedido do cliente: "o botão de garantir minha
              presença deveria ter cor em volta dele; ele não tem cor nenhuma,
              só tem o texto").

              Ele era `v2-cta-texto` — link com seta, criado em 12/08 quando a
              página tinha SEIS botões azuis idênticos competindo entre si. A
              correção de então continua certa no princípio e errada na dose:
              rebaixar quatro dos seis apagou a ação em dobras onde ela é o
              único caminho para a inscrição. Volta a ser botão; o rótulo
              continua sendo um só na página inteira, que era o ponto real. */}
          <a href={ANCORA_INSCRICAO} className="btn btn-rota">
            {textoCta}
            <span className="btn-seta" aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </section>
  )
}
